import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { Typography } from '@mui/material';

import PreviewModal from './PreviewModal';

const AttachmentContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    backgroundColor: theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[100],
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: `1px dashed ${theme.palette.divider}`,
    display: attachments => attachments.length > 0 ? 'flex' : 'none',
    flexWrap: 'wrap',
    height: attachments => attachments.length > 0 ? 'auto' : 0,
    gap: theme.spacing(1),
    overflow: 'hidden',
    marginBottom: attachments => attachments.length > 0 ? theme.spacing(1) : 0,
}));

const AttachmentGrid = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
}));

const AttachmentWrapper = styled(Box)(({ theme }) => ({
    position: 'relative',
    cursor: 'pointer',
    '&:hover .deleteButton': {
        display: 'flex'
    }
}));

const ImagePreview = styled('img')(({ theme }) => ({
    width: '96px',
    height: '96px',
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        opacity: 0.8,
        transform: 'scale(1.05)',
    }
}));

const DocumentPreview = styled(Box)(({ theme }) => ({
    width: '96px',
    height: '96px',
    borderRadius: theme.shape.borderRadius,
    backgroundColor:
        theme.palette.mode === 'dark'
            ? theme.palette.grey[800]
            : theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1),
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        opacity: 0.8,
        transform: 'scale(1.05)',
    }
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[800],
    padding: 4,
    display: 'none',
    color: theme.palette.common.white,
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.grey[900]
            : theme.palette.grey[800],
    },
    '& .MuiSvgIcon-root': {
        fontSize: '1rem'
    },
    zIndex: 1,
}));

const AttachmentPanel = ({ attachments, onRemove }) => {
    const [selectedAttachment, setSelectedAttachment] = useState(null);

    if (!attachments || attachments.length === 0) return null;

    const handleAttachmentClick = (attachment, event) => {
        if (event.target.closest('.deleteButton')) {
            return;
        }
        setSelectedAttachment(attachment);
    };

    const isImageType = (attachment) => {
        // Check both type and mimetype since either might be present
        const mimeType = attachment.mimetype || attachment.type;
        return mimeType && typeof mimeType === 'string' && mimeType.startsWith('image/');
    };

    return (
        <AttachmentContainer attachments={attachments}>
            <AttachmentGrid>
                {attachments.map((attachment, index) => (
                    <AttachmentWrapper
                        key={index}
                        onClick={(event) => handleAttachmentClick(attachment, event)}
                    >
                        {isImageType(attachment) ? (
                            <ImagePreview
                                src={attachment.preview}
                                alt={attachment.originalName || 'Preview'}
                            />
                        ) : (
                            <DocumentPreview>
                                {attachment.type === 'application/pdf' || attachment.mimetype === 'application/pdf' ? (
                                    <PictureAsPdfIcon sx={{ fontSize: 24, mb: 0.5 }} />
                                ) : (
                                    <InsertDriveFileIcon sx={{ fontSize: 24, mb: 0.5 }} />
                                )}
                                <Typography
                                    variant="caption"
                                    align="center"
                                    sx={{
                                        width: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {attachment.originalName || 'Document'}
                                </Typography>
                            </DocumentPreview>
                        )}
                        <DeleteButton
                            className="deleteButton"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove(index);
                            }}
                            size="small"
                        >
                            <CloseIcon />
                        </DeleteButton>
                    </AttachmentWrapper>
                ))}
            </AttachmentGrid>

            {selectedAttachment && (
                <PreviewModal
                    attachment={selectedAttachment}
                    onClose={() => setSelectedAttachment(null)}
                />
            )}
        </AttachmentContainer>
    );
};

export default AttachmentPanel;